// "Export" on /workspace/:id: download the chapters (YouTube-ready), the transcript as text or subtitles,
// the chats, or everything in one Markdown file. The transcript (exportTranscript, fetched on first use)
// exists for uploads, long YouTube videos, and short ones that have YouTube captions.
import React, { useEffect, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faCircleNotch, faLock } from '@fortawesome/free-solid-svg-icons';
import { auth, db } from '../../../firebase';
import { buildThreads } from '../chat/chatModel';
import { track } from '../../../analytics/track';
import { chaptersText, toSrt, toVtt, transcriptText, chatMarkdown, everythingMarkdown, fileName } from './exportFormats';
import './exportMenu.css';

function download(text, name, type = 'text/plain') {
  const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// canFetchCaptions: a short YouTube video processed before captions transcripts were kept; the first
// transcript download asks ensureCaptionTranscript to fetch YouTube's captions now.
export default function ExportMenu({
  contentId, title, timestamps, videoUrl, legacyChat, transcriptAvailable, canFetchCaptions, previewLocked, onUnlock,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const transcriptRef = useRef(null); // { segments, duration } once fetched
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const getTranscript = async () => {
    if (!transcriptRef.current) {
      if (!transcriptAvailable && canFetchCaptions) {
        const { data: got } = await httpsCallable(getFunctions(), 'ensureCaptionTranscript', { timeout: 130000 })({ contentId: `workspace-${contentId}` });
        if (!got.available) throw new Error('YouTube has no captions for this video, so there’s no transcript to download.');
      }
      const { data } = await httpsCallable(getFunctions(), 'exportTranscript')({ contentId: `workspace-${contentId}` });
      transcriptRef.current = data;
    }
    return transcriptRef.current;
  };

  const getThreads = async () => {
    const snap = await getDoc(doc(db, 'users', auth.currentUser.uid, 'workspaceChats', `workspace-${contentId}`));
    return buildThreads({ chatDoc: snap.exists() ? snap.data() : null, workspace: legacyChat || {} });
  };

  const run = async (kind, format, make) => {
    setBusy(`${kind}.${format}`);
    setError('');
    try {
      const result = await make();
      if (!result) { setError('Nothing to export yet.'); return; }
      download(result.text, fileName(result.name || title, format), result.type);
      track('export_downloaded', { kind, format });
      setOpen(false);
    } catch (e) {
      setError(e?.message && /Pro|captions/.test(e.message) ? e.message : 'That export didn’t work. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const chapters = chaptersText(timestamps);
  const items = [
    {
      group: 'Chapters',
      rows: [{ kind: 'chapters', format: 'txt', label: 'YouTube chapters (.txt)', disabled: !chapters,
        make: async () => chapters && { text: `${chapters}\n`, name: `${title} chapters` } }],
    },
    {
      group: 'Transcript',
      note: previewLocked ? 'locked' : !transcriptAvailable && !canFetchCaptions ? 'This video has no transcript: YouTube has no captions for it.' : null,
      rows: [
        { kind: 'transcript', format: 'txt', label: 'Transcript (.txt)', make: async () => { const t = await getTranscript(); return t.available && { text: `${transcriptText(t.segments)}\n`, name: `${title} transcript` }; } },
        { kind: 'transcript', format: 'srt', label: 'Subtitles (.srt)', make: async () => { const t = await getTranscript(); return t.available && { text: toSrt(t.segments, t.duration), type: 'application/x-subrip' }; } },
        { kind: 'transcript', format: 'vtt', label: 'Subtitles (.vtt)', make: async () => { const t = await getTranscript(); return t.available && { text: toVtt(t.segments, t.duration), type: 'text/vtt' }; } },
      ].map(r => ({ ...r, disabled: previewLocked || (!transcriptAvailable && !canFetchCaptions) })),
    },
    {
      group: 'Chats',
      rows: [{ kind: 'chats', format: 'md', label: 'All chats (.md)', make: async () => { const md = chatMarkdown(await getThreads()); return md && { text: `# ${title}\n\n${md}\n`, name: `${title} chats`, type: 'text/markdown' }; } }],
    },
    {
      group: 'Everything',
      rows: [{ kind: 'everything', format: 'md', label: 'Chapters, chats and transcript (.md)', make: async () => {
        const [threads, t] = await Promise.all([getThreads(), transcriptAvailable && !previewLocked ? getTranscript() : null]);
        return { text: everythingMarkdown({ title, url: videoUrl, timestamps, threads, segments: t?.segments }), type: 'text/markdown' };
      } }],
    },
  ];

  return (
    <div className="exm" ref={boxRef}>
      <button type="button" className="exm-btn" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}>
        <FontAwesomeIcon icon={faDownload} /> <span className="exm-btn-label">Export</span>
      </button>
      {open && (
        <div className="exm-menu" role="menu">
          {items.map(g => (
            <div key={g.group} className="exm-group">
              <p className="exm-group-label">{g.group}</p>
              {g.note === 'locked' ? (
                <button type="button" className="exm-locked" onClick={() => { setOpen(false); onUnlock?.(); }}>
                  <FontAwesomeIcon icon={faLock} /> Unlock with Pro to export the full preview
                </button>
              ) : g.note && <p className="exm-note">{g.note}</p>}
              {g.rows.map(r => (
                <button
                  key={r.format + r.label}
                  type="button"
                  role="menuitem"
                  className="exm-item"
                  disabled={r.disabled || !!busy}
                  onClick={() => run(r.kind, r.format, r.make)}
                >
                  {r.label}
                  {busy === `${r.kind}.${r.format}` && <FontAwesomeIcon icon={faCircleNotch} spin className="exm-spin" />}
                </button>
              ))}
            </div>
          ))}
          {error && <p className="exm-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
